import { useForm, Controller } from 'react-hook-form'
import {
    Box,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Stack,
    Button,
    Heading,
    Text,
    Select,
    FormHelperText
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import ReactSelect from 'react-select'
import CustomLink from '@/components/CustomLink'
import PasswordInput from '@/components/PasswordInput'
import { useBranches } from '@/shared/api/hooks/useBranches'
import SignUpSchema from '@/modules/signup-page/signup-schema'
import { useSignupHandler } from './hooks/useSignupHandler'
import { PARENT_CHILD_RELATIONSHIPS, PARENT_ROLES } from './types'

export default function SignupPage() {
    const { branches, loading: branchLoading } = useBranches()
    const { onSubmit, isSubmitting } = useSignupHandler()
    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(SignUpSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            picture: '',
            relationship: '',
            role: '',
            branch: {},
            phoneNumber: ''
        }
    })

    return (
        <>
            <Stack align={'center'}>
                <Heading fontSize={'2xl'} textAlign={'center'}>
                    Account Sign up
                </Heading>
                <Text fontSize={{ base: 'sm', sm: 'md' }} color={'gray.600'}>
                    You can register as teacher or parent ✌️
                </Text>
            </Stack>
            <Box rounded={'lg'} bg={'white'} boxShadow={'lg'} p={8}>
                <Stack spacing={4}>
                    <Stack direction={{ base: 'column', md: 'row' }}>
                        <FormControl isInvalid={Boolean(errors.firstName)} isRequired>
                            <FormLabel>First Name</FormLabel>
                            <Input
                                type="text"
                                id="firstName"
                                {...register('firstName')}
                            />
                            <FormErrorMessage>
                                {errors.firstName && errors.firstName.message}
                            </FormErrorMessage>
                        </FormControl>
                        <FormControl isInvalid={Boolean(errors.lastName)} isRequired>
                            <FormLabel>Surname</FormLabel>
                            <Input type="text" id="lastName" {...register('lastName')} />
                            <FormErrorMessage>
                                {errors.lastName && errors.lastName.message}
                            </FormErrorMessage>
                        </FormControl>
                    </Stack>
                    <FormControl isInvalid={Boolean(errors.email)} isRequired>
                        <FormLabel>Email address</FormLabel>
                        <Input type="email" id="email" {...register('email')} />

                        <FormErrorMessage>
                            {errors.email && errors.email.message}
                        </FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={Boolean(errors.password)} isRequired>
                        <FormLabel>Password</FormLabel>
                        <Controller
                            name="password"
                            control={control}
                            render={({ field }) => <PasswordInput {...field} />}
                        />
                        <FormErrorMessage>
                            {errors.password && errors.password.message}
                        </FormErrorMessage>
                        <FormHelperText>
                            Password must be minimum of 6 characters 🚨
                        </FormHelperText>
                    </FormControl>
                    <FormControl isInvalid={Boolean(errors.phoneNumber)} isRequired>
                        <FormLabel>Phone Number</FormLabel>
                        <Input type="tel" id="phoneNumber" {...register('phoneNumber')} />

                        <FormErrorMessage>
                            {errors.phoneNumber && errors.phoneNumber.message}
                        </FormErrorMessage>
                    </FormControl>
                    <Stack direction={{ base: 'column', md: 'row' }}>
                        <FormControl isInvalid={Boolean(errors.relationship)} isRequired>
                            <FormLabel>Child Relationship</FormLabel>
                            <Select id="relationship" {...register('relationship')}>
                                <option value={PARENT_CHILD_RELATIONSHIPS.FATHER}>
                                    Father
                                </option>
                                <option value={PARENT_CHILD_RELATIONSHIPS.MOTHER}>
                                    Mother
                                </option>
                                <option value={PARENT_CHILD_RELATIONSHIPS.GUARDIAN}>
                                    Guardian
                                </option>
                                <option value={PARENT_CHILD_RELATIONSHIPS.NONE}>
                                    None
                                </option>
                            </Select>

                            <FormErrorMessage>
                                {errors.relationship && errors.relationship.message}
                            </FormErrorMessage>
                        </FormControl>
                        <FormControl isInvalid={Boolean(errors.role)} isRequired>
                            <FormLabel>Signing up as</FormLabel>
                            <Select id="role" {...register('role')}>
                                <option value={PARENT_ROLES.PARENT}>Parent only</option>
                                <option value={PARENT_ROLES.TEACHER}>Teacher only</option>
                                <option value={PARENT_ROLES.PARENT_TEACHER}>
                                    Both Parent & Children Church Teacher
                                </option>
                            </Select>

                            <FormErrorMessage>
                                {errors.role && errors.role.message}
                            </FormErrorMessage>
                        </FormControl>
                    </Stack>
                    <FormControl isInvalid={Boolean(errors.branch)} isRequired>
                        <FormLabel>Church Branch</FormLabel>
                        <Controller
                            name="branch"
                            control={control}
                            render={({ field }) => (
                                <ReactSelect
                                    {...field}
                                    isSearchable
                                    placeholder="choose branch"
                                    options={branches}
                                    isLoading={branchLoading}
                                />
                            )}
                        />
                        <FormErrorMessage>
                            {errors.branch && errors.branch.message}
                        </FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={Boolean(errors.picture)} isRequired>
                        <FormLabel>My Photo</FormLabel>
                        <Input
                            type="file"
                            id="picture"
                            accept="image/*"
                            {...register('picture')}
                        />
                        <FormErrorMessage>
                            {errors.picture && errors.picture.message}
                        </FormErrorMessage>
                        <FormHelperText>
                            Maximum image size allowed is 4MB 😉
                        </FormHelperText>
                    </FormControl>
                    <Stack spacing={10} pt={2}>
                        <Button
                            type="submit"
                            loadingText="Submitting"
                            size="lg"
                            isLoading={isSubmitting}
                            onClick={handleSubmit((formValue) => {
                                onSubmit(formValue, reset)
                            })}
                        >
                            Sign up
                        </Button>
                    </Stack>
                    <Stack pt={6}>
                        <Text align={'center'}>
                            Do you have parent account?{' '}
                            <CustomLink href="/">Login</CustomLink>
                        </Text>
                    </Stack>
                </Stack>
            </Box>
        </>
    )
}
